import { createSlice, createAsyncThunk } from '@reduxjs/toolkit';
import { recordApi } from 'app/shared-components/client-records/utils/recordApi';

// Async thunks
export const fetchRecords = createAsyncThunk(
  'clientRecords/fetchRecords',
  async ({ clientId, filters = {}, forceRefresh = false }, { getState }) => {
    const state = getState();
    const cacheKey = `${clientId}-${JSON.stringify(filters)}`;
    
    // Return cached data if available and not forcing refresh
    if (!forceRefresh && state.clientRecords.recordsCache[cacheKey]) {
      return { 
        records: state.clientRecords.recordsCache[cacheKey],
        fromCache: true,
        cacheKey
      };
    }
    
    const response = await recordApi.getRecords(clientId, filters);
    return { 
      records: response,
      fromCache: false,
      cacheKey
    };
  }
);

export const fetchRecord = createAsyncThunk(
  'clientRecords/fetchRecord',
  async (params) => {
    // Handle both old format (just recordId) and new format ({ recordId, recordType })
    const recordId = typeof params === 'string' ? params : params.recordId;
    const recordType = typeof params === 'object' ? params.recordType : undefined;
    
    // Construct full key format: RecordType:id
    const fullKey = recordType ? `${recordType}:${recordId}` : recordId;

    const response = await recordApi.getRecord(fullKey);
    return response;
  }
);

export const createRecord = createAsyncThunk(
  'clientRecords/createRecord',
  async (recordData, { rejectWithValue }) => {
    try {
      const response = await recordApi.createRecord(recordData);
      return response;
    } catch (error) {
      return rejectWithValue(error.message || 'Failed to create record');
    }
  }
);

export const updateRecord = createAsyncThunk(
  'clientRecords/updateRecord',
  async ({ recordId, recordData }) => {
    const response = await recordApi.updateRecord(recordId, recordData);
    return response;
  }
);

export const deleteRecord = createAsyncThunk(
  'clientRecords/deleteRecord',
  async (recordId) => {
    await recordApi.deleteRecord(recordId);
    return recordId;
  }
);

export const fetchSchema = createAsyncThunk(
  'clientRecords/fetchSchema',
  async (recordType, { getState, rejectWithValue }) => {
    const state = getState();
    
    // Check if schema is already cached
    if (state.clientRecords.schemas[recordType]) {
      return { recordType, schema: state.clientRecords.schemas[recordType], fromCache: true };
    }
    
    try {
      const response = await recordApi.getSchema(recordType);
      return { recordType, schema: response };
    } catch (error) {
      return rejectWithValue(error.message);
    }
  }
);

export const fetchPrePopulatedData = createAsyncThunk(
  'clientRecords/fetchPrePopulatedData',
  async ({ templateId, clientId }) => {
    const response = await recordApi.getPrePopulatedData(templateId, clientId);
    return response;
  }
);

export const signRecord = createAsyncThunk(
  'clientRecords/signRecord',
  async ({ recordId, signatureData }) => {
    const response = await recordApi.signRecord(recordId, signatureData);
    return response;
  }
);

export const unsignRecord = createAsyncThunk(
  'clientRecords/unsignRecord',
  async (recordId) => {
    const response = await recordApi.unsignRecord(recordId);
    return response;
  }
);



const initialState = {
  records: [],
  recordsCache: {}, // Cache records by filter key
  currentRecord: null,
  schemas: {},
  enums: {},
  loading: false,
  error: null,
  totalCount: 0,
  filters: {
    searchTerm: '',
    recordTypes: [],
    templateId: '',
    createdAfter: null,
    createdBefore: null,
    limit: 20,
    offset: 0
  },
  // Current tab state
  currentTab: {
    recordType: null,
    keyType: null,
    templateId: null,
    schema: null,
    enums: {}
  },
  prePopulatedData: null,
  // Dialog states
  historyDialogRecord: null,
  taskFormRecord: null,
  taskListRecord: null,
  roiDialogRecord: null
};

const clientRecordsSlice = createSlice({
  name: 'clientRecords',
  initialState,
  reducers: {
    setFilters: (state, action) => {
      state.filters = { ...state.filters, ...action.payload };
    },
    clearCurrentRecord: (state) => {
      state.currentRecord = null;
    },
    clearError: (state) => {
      state.error = null;
    },
    setCurrentTab: (state, action) => {
      const { recordType, keyType, templateId, schema, enums } = action.payload;
      state.currentTab = {
        recordType,
        keyType,
        templateId,
        schema,
        enums: enums || {}
      };
      // Clear records when switching tabs but preserve filters
      state.records = [];
      state.totalCount = 0;
    },
    clearRecords: (state) => {
      state.records = [];
      state.totalCount = 0;
    },
    clearPrePopulatedData: (state) => {
      state.prePopulatedData = null;
    },
    setHistoryDialogRecord: (state, action) => {
      state.historyDialogRecord = action.payload;
    },
    setTaskFormRecord: (state, action) => {
      state.taskFormRecord = action.payload;
    },
    setTaskListRecord: (state, action) => {
      state.taskListRecord = action.payload;
    },
    setRoiDialogRecord: (state, action) => {
      state.roiDialogRecord = action.payload;
    }
  },
  extraReducers: (builder) => {
    builder
      // Fetch records
      .addCase(fetchRecords.pending, (state, action) => {
        // Only show loading if not from cache
        if (!action.meta.arg.fromCache) {
          state.loading = true;
        }
        state.error = null;
      })
      .addCase(fetchRecords.fulfilled, (state, action) => {
        state.loading = false;
        const { records, fromCache, cacheKey } = action.payload;
        
        state.records = records;
        state.totalCount = records.length;
        
        // Cache the results if not from cache
        if (!fromCache) {
          state.recordsCache[cacheKey] = records;
        }
      })
      .addCase(fetchRecords.rejected, (state, action) => {
        state.loading = false;
        state.error = action.error.message;
      })
      
      // Fetch single record
      .addCase(fetchRecord.fulfilled, (state, action) => {
        state.currentRecord = action.payload;
      })
      
      // Create record
      .addCase(createRecord.pending, (state) => {
        state.loading = true;
        state.error = null;
      })
      .addCase(createRecord.fulfilled, (state, action) => {
        state.loading = false;
        if (action.payload) {
          state.records.unshift(action.payload);
          state.totalCount += 1;
        }
        // Clear all caches to force refetch when navigating
        state.recordsCache = {};
      })
      .addCase(createRecord.rejected, (state, action) => {
        state.loading = false;
        state.error = action.error.message;

      })
      
      // Update record
      .addCase(updateRecord.fulfilled, (state, action) => {
        const index = state.records.findIndex(r => r.id === action.payload.id);
        if (index !== -1) {
          state.records[index] = action.payload;
        }
        if (state.currentRecord?.id === action.payload.id) {
          state.currentRecord = action.payload;
        }
        // Clear all caches to ensure consistency
        state.recordsCache = {};
      })
      
      // Delete record
      .addCase(deleteRecord.fulfilled, (state, action) => {
        // Extract just the ID part from the full key (e.g., "ClientInsurance:uuid" -> "uuid")
        const recordId = action.payload.includes(':') ? action.payload.split(':')[1] : action.payload;
        state.records = state.records.filter(r => r.id !== recordId);
        state.totalCount -= 1;
        if (state.currentRecord?.id === recordId) {
          state.currentRecord = null;
        }
        // Clear all caches to ensure consistency
        state.recordsCache = {};
      })
      
      // Fetch schema
      .addCase(fetchSchema.fulfilled, (state, action) => {
        state.schemas[action.payload.recordType] = action.payload.schema;
      })
      .addCase(fetchSchema.rejected, (state, action) => {
        state.error = action.payload;
      })
      
      // Fetch pre-populated data
      .addCase(fetchPrePopulatedData.fulfilled, (state, action) => {
        state.prePopulatedData = action.payload;
      })
      .addCase(fetchPrePopulatedData.rejected, (state, action) => {
        state.error = action.error.message;
      })
      
      // Sign record
      .addCase(signRecord.fulfilled, (state, action) => {
        const index = state.records.findIndex(r => r.id === action.payload.id);
        if (index !== -1) {
          state.records[index] = action.payload;
        }
        if (state.currentRecord?.id === action.payload.id) {
          state.currentRecord = action.payload;
        }
      })
      
      // Unsign record
      .addCase(unsignRecord.fulfilled, (state, action) => {
        const index = state.records.findIndex(r => r.id === action.payload.id);
        if (index !== -1) {
          state.records[index] = action.payload;
        }
        if (state.currentRecord?.id === action.payload.id) {
          state.currentRecord = action.payload;
        }
      })

  }
});

export const { setFilters, clearCurrentRecord, clearError, setCurrentTab, clearRecords, clearPrePopulatedData, setHistoryDialogRecord, setTaskFormRecord, setTaskListRecord, setRoiDialogRecord } = clientRecordsSlice.actions;

// Selectors
export const selectRecords = (state) => state.clientRecords.records;
export const selectCurrentRecord = (state) => state.clientRecords.currentRecord;
export const selectRecordsLoading = (state) => state.clientRecords.loading;
export const selectRecordsError = (state) => state.clientRecords.error;
export const selectTotalCount = (state) => state.clientRecords.totalCount;
export const selectFilters = (state) => state.clientRecords.filters;
export const selectSchema = (recordType) => (state) => state.clientRecords.schemas[recordType];
export const selectEnums = (recordType) => (state) => state.clientRecords.enums[recordType];
export const selectCurrentTab = (state) => state.clientRecords.currentTab;
export const selectPrePopulatedData = (state) => state.clientRecords.prePopulatedData;
export const selectHistoryDialogRecord = (state) => state.clientRecords.historyDialogRecord;
export const selectTaskFormRecord = (state) => state.clientRecords.taskFormRecord;
export const selectTaskListRecord = (state) => state.clientRecords.taskListRecord;
export const selectRoiDialogRecord = (state) => state.clientRecords.roiDialogRecord;

export default clientRecordsSlice.reducer;